import { useForm, useWatch } from "react-hook-form";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import Input from "components/ui/input/Input";
import { betterAuthErrorText } from "lib/auth/authClient";
import { useEnableTwoFactor } from "lib/hooks/api/auth/useEnableTwoFactor";
import Label from "../../ui/input/Label";
import { ErrorText, MfaForm, MfaSetup } from "./utils";

type FormValues = { password: string };

export default function TurnOnMFA({
  companyRequires2FA,
  setSetup,
}: {
  companyRequires2FA: boolean;
  setSetup: React.Dispatch<React.SetStateAction<MfaSetup | null>>;
}) {
  const {
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: { password: "" },
  });

  // Disable the submit until a password is entered.
  const password = useWatch({ control, name: "password" });

  const { mutate: enableMFA, isLoading } = useEnableTwoFactor({
    onSuccess: (data) => setSetup(data),
    // Surface server errors (e.g. wrong password) as an error on the password field (below the input).
    onError: (error) =>
      setError("password", { type: "server", message: betterAuthErrorText(error) }),
  });

  const onEnable = handleSubmit((values) => enableMFA({ password: values.password }));

  return (
    <MfaForm onSubmit={onEnable}>
      <Text>
        Two-factor authentication is <strong>off</strong>.{" "}
        {companyRequires2FA
          ? "Your organisation requires it — set it up to continue."
          : "Add an authenticator app for extra security."}
      </Text>
      <Flex column gap="4px">
        <Label htmlFor="password">Password</Label>
        <Input
          name="password"
          control={control}
          type="password"
          placeholder="Password"
          rules={{ required: { value: true, message: "Password is required" } }}
        />
        {errors.password && <ErrorText>{errors.password.message}</ErrorText>}
      </Flex>
      <Button buttonType="primary" type="submit" loading={isLoading} disabled={!password}>
        Set up two-factor authentication
      </Button>
    </MfaForm>
  );
}
